import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { mt, MBtn, MCard, getGps, fmtT } from "./shared";

const ACTIONS = [
  ["confirm_address", "CONFIRM ADDRESS", mt.green, false],
  ["report_wrong_address", "REPORT WRONG ADDRESS", mt.amber, true],
  ["correct_map_pin", "CORRECT MAP PIN", mt.cyan, false],
  ["add_entrance", "ADD ENTRANCE INFO", mt.blue, false],
  ["report_hazard", "REPORT HAZARD", mt.red, true],
  ["confirm_alert", "CONFIRM EXISTING ALERT", mt.green, false],
  ["dispute_alert", "DISPUTE ALERT", mt.amber, true],
  ["request_supervisor_review", "REQUEST SUPERVISOR REVIEW", mt.purple, false],
];

export function MobileAddressIntel({ user, defaultAddress, onBack }) {
  const [addr, setAddr] = useState(defaultAddress || "");
  const [intel, setIntel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteFor, setNoteFor] = useState(null); // [action, label, requiresNote]
  const [note, setNote] = useState("");
  const [pending, setPending] = useState([]);
  const isSup = ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user.role);

  const lookup = async (a) => {
    const q = (a || addr).trim();
    if (!q) return;
    setBusy(true);
    try {
      const r = await client.get("/address-intel", { params: { address: q } });
      setIntel(r.data);
    } catch { toast.error("Lookup failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (defaultAddress) lookup(defaultAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddress]);
  useEffect(() => {
    if (isSup) client.get("/mobile/address-actions", { params: { status: "pending_review" } })
      .then((r) => setPending(r.data || [])).catch(() => {});
  }, [isSup]);

  const submit = async (action, label, txt) => {
    const gps = await getGps();
    try {
      const r = await client.post("/mobile/address-actions",
        { address: addr.trim(), action, note: txt || "", ...gps });
      toast.success(r.data.status === "approved"
        ? `${label} recorded (audited)` : `${label} submitted — supervisor review required`);
      setNoteFor(null); setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
  };

  const [reviewNotes, setReviewNotes] = useState({});
  const review = async (f, decision) => {
    try {
      await client.post(`/mobile/address-actions/${f.id}/review`, { decision, note: reviewNotes[f.id] || "" });
      toast.success(`Submission ${decision}d`);
      setPending((p) => p.filter((x) => x.id !== f.id));
    } catch { toast.error("Review failed"); }
  };

  return (
    <div data-testid="mobile-address-intel" className="flex flex-col gap-3 p-3 pb-28">
      <div className="flex items-center justify-between">
        <span className="font-head font-bold text-sm" style={{ color: mt.text }}>ADDRESS INTELLIGENCE</span>
        <MBtn small outline color={mt.dim} onClick={onBack} testid="mobile-addr-back">BACK</MBtn>
      </div>
      <div className="flex gap-2">
        <input data-testid="mobile-addr-input" value={addr} onChange={(e) => setAddr(e.target.value)}
          placeholder="Enter address…" className="flex-1 px-3 py-3 rounded-lg font-mono text-sm"
          style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
        <MBtn small color={mt.blue} disabled={busy || !addr.trim()} onClick={() => lookup()} testid="mobile-addr-lookup">
          {busy ? "…" : "LOOKUP"}
        </MBtn>
      </div>

      {intel && intel.banner && (
        <MCard testid="mobile-addr-banner" style={{ border: `2px solid ${mt.amber}` }}>
          <span className="text-xs font-head font-bold" style={{ color: mt.amber }}>{intel.banner}</span>
        </MCard>
      )}
      {intel && (
        <MCard testid="mobile-addr-summary">
          <div className="text-xs font-mono mb-1" style={{ color: mt.text }}>
            {intel.master?.full_norm} · {intel.total_calls} prior calls
          </div>
          <div className="text-[11px] font-mono" style={{ color: mt.dim }}>
            24h: {intel.counts?.["24h"]} · 7d: {intel.counts?.["7d"]} · 30d: {intel.counts?.["30d"]} · 12m: {intel.counts?.["12m"]}
          </div>
          {(intel.premise_hazards || []).length > 0 && (
            <div className="mt-2 py-1.5 px-2 rounded text-[11px] font-mono" style={{ background: "#3b0d0d", color: "#FCA5A5" }}>
              ⚠️ HAZARDS: {(intel.premise_hazards || []).map((h) => h.type || h.label || "").filter(Boolean).join(", ") || intel.premise_hazards.length}
            </div>
          )}
          {(intel.violent_recent || []).length > 0 && (
            <div className="mt-1.5 text-[11px] font-mono" style={{ color: "#FCA5A5" }}>
              🔴 Violent (12m): {(intel.violent_recent || []).slice(0, 3).map((v) => v.type).join("; ")}
            </div>
          )}
        </MCard>
      )}

      {intel && (
        <>
          <div className="text-[11px] font-head font-bold uppercase" style={{ color: mt.dim }}>Field Actions (audited)</div>
          <div className="grid grid-cols-2 gap-2">
            {ACTIONS.map(([k, label, color, needsNote]) => (
              <MBtn key={k} small full outline color={color} testid={`mobile-addr-${k}`}
                onClick={() => (needsNote || k === "add_entrance" ? setNoteFor([k, label, needsNote]) : submit(k, label, ""))}>
                {label}
              </MBtn>
            ))}
          </div>
          <div className="text-[10px] font-mono" style={{ color: mt.dim }}>
            Permanent changes require supervisor review before taking effect.
          </div>
        </>
      )}

      {noteFor && (
        <div className="fixed inset-0 z-[860] flex items-end" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="w-full rounded-t-2xl p-4 pb-8" style={{ background: mt.bg, border: `1px solid ${mt.border}` }}>
            <div className="font-head font-bold text-sm mb-2" style={{ color: mt.text }}>{noteFor[1]}</div>
            <textarea data-testid="mobile-addr-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={noteFor[2] ? "Describe the issue (required)…" : "Details (optional)…"} rows={3}
              className="w-full mb-3 px-3 py-2.5 rounded-lg font-mono text-sm"
              style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
            <div className="grid grid-cols-2 gap-2">
              <MBtn full color={mt.green} disabled={noteFor[2] && !note.trim()}
                onClick={() => submit(noteFor[0], noteFor[1], note.trim())} testid="mobile-addr-submit">SUBMIT</MBtn>
              <MBtn full outline color={mt.dim} onClick={() => { setNoteFor(null); setNote(""); }} testid="mobile-addr-cancel">CANCEL</MBtn>
            </div>
          </div>
        </div>
      )}

      {isSup && pending.length > 0 && (
        <>
          <div className="text-[11px] font-head font-bold uppercase mt-2" style={{ color: mt.amber }}>
            Pending Supervisor Review ({pending.length})
          </div>
          {pending.map((f) => (
            <MCard key={f.id} testid={`mobile-addr-pending-${f.id}`} style={{ border: `1px solid ${mt.amber}` }}>
              <div className="text-xs font-mono" style={{ color: mt.text }}>
                {f.action.replace(/_/g, " ").toUpperCase()} — {f.address}
              </div>
              <div className="text-[11px] font-mono mt-0.5" style={{ color: mt.dim }}>
                by {f.by} · {fmtT(f.ts)}{f.note ? ` · "${f.note}"` : ""}
              </div>
              <input data-testid={`mobile-addr-review-note-${f.id}`} value={reviewNotes[f.id] || ""}
                onChange={(e) => setReviewNotes((n) => ({ ...n, [f.id]: e.target.value }))}
                placeholder="Review note (optional)…" className="w-full mt-2 px-3 py-2 rounded-lg font-mono text-xs"
                style={{ background: mt.surface, border: `1px solid ${mt.border}`, color: mt.text }} />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <MBtn small color={mt.green} onClick={() => review(f, "approve")} testid={`mobile-addr-approve-${f.id}`}>APPROVE</MBtn>
                <MBtn small outline color={mt.red} onClick={() => review(f, "reject")} testid={`mobile-addr-reject-${f.id}`}>REJECT</MBtn>
              </div>
            </MCard>
          ))}
        </>
      )}
    </div>
  );
}
